import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { Box, Checkbox, TextField, Typography } from "@mui/material";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSingleNivel } from "../../../../context/SuperAdmin/OASContexts/SingleNivelContext";
import { iDataAsignatura } from "../../../../interfaces/SuperAdmin/Pages/iDataAsignatura";

interface Props {
  selectedAsignaturas: string[];
  setSelectedAsignaturas: Dispatch<SetStateAction<string[]>>;
}

const OtherAsignsMenu: FC<Props> = ({
  selectedAsignaturas,
  setSelectedAsignaturas,
}) => {
  const { allAsignaturas, nivelAsigns, isAdding } = useSingleNivel();

  const [missingAsigns, setMissingAsigns] = useState<iDataAsignatura[]>([]);

  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    // Hacemos un filtro de todas las asignaturas. Evaluamos si alguna de las asignaturas ya estan en el nivel.
    //De esta forma dejamos solo las asignaturas que no han sido elegidos
    const newArray = allAsignaturas.filter(
      (asign) =>
        !nivelAsigns.some((item) => item.id_asignatura === asign.id_asignatura)
    );
    setMissingAsigns(newArray);
  }, [allAsignaturas, nivelAsigns]);

  // Función para manejar la selección/deselección individual
  const handleCheckboxChange = (id: string) => {
    setSelectedAsignaturas((prev) =>
      prev.includes(id) ? prev.filter((comp) => comp !== id) : [...prev, id]
    );
  };

  // Función para seleccionar o deseleccionar todos
  const toggleSelectAll = () => {
    if (selectedAsignaturas.length > 0) {
      setSelectedAsignaturas([]); // Si todos están seleccionados, deselecciona
    } else {
      setSelectedAsignaturas(missingAsigns.map((asign) => asign.id_asignatura)); // Selecciona todos
    }
  };

  // Filtro de asignaturas por búsqueda
  const filteredAsignaturas = useMemo(() => {
    const normalizedSearch = searchText.toLowerCase().trim();
    return missingAsigns.filter((u) =>
      u.nombre.toLowerCase().includes(normalizedSearch)
    );
  }, [searchText, missingAsigns]);

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid rgba(51, 107, 135, 0.5)", // Borde con opacidad
        borderRadius: "8px", // Bordes redondeados
        padding: "8px 16px", // Padding general
        transition: "all 0.3s ease", // Suaviza la transición
      }}
    >
      {/* Título */}
      <Typography variant="h6" component="h2" color="#000">
        Otras asignaturas{" "}
        <Typography component="span" color={"#73767E"} fontSize={"20px"}>
          {selectedAsignaturas.length > 0
            ? `(${selectedAsignaturas.length}/${missingAsigns.length})`
            : `(${missingAsigns.length})`}
        </Typography>
      </Typography>

      {/* Search bar */}
      <TextField
        fullWidth
        placeholder="Buscar por nombre"
        size="small"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{
          marginTop: "8px",
          backgroundColor: "#F9F9F9",
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
        }}
      />

      {/* Botón de Seleccionar/Deseleccionar todos */}
      {missingAsigns.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: "8px",
            py: "14px",
            marginTop: "8px",
            borderRadius: "6px",
            fontFamily: "Montserrat",
            fontWeight: "bold",
            backgroundColor: "#336B87",
            color: "#FFF",
          }}
        >
          <Box
            sx={{
              width: "40px",
              display: "flex",
              justifyContent: "center",
              mr: "8px",
            }}
          >
            <Checkbox
              checked={
                selectedAsignaturas.length === missingAsigns.length &&
                missingAsigns.length > 0
              }
              indeterminate={
                selectedAsignaturas.length > 0 &&
                selectedAsignaturas.length < missingAsigns.length
              }
              onChange={toggleSelectAll}
              disabled={isAdding}
              icon={<CheckBoxOutlineBlankIcon sx={{ color: "#FFF" }} />}
              checkedIcon={<CheckBoxIcon sx={{ color: "#FFF" }} />}
              indeterminateIcon={
                <IndeterminateCheckBoxIcon sx={{ color: "#FFF" }} />
              }
              sx={{
                padding: 0,
              }}
            />
          </Box>
          <Typography sx={{ flex: 1 }}>Nombre</Typography>
        </Box>
      )}

      {/* Contenido con scroll si excede los 350px */}
      <Box
        sx={{
          marginTop: "8px",
          paddingTop: "8px",
          borderTop: "1px solid rgba(51, 107, 135, 0.5)", // Separador sutil
          maxHeight: "350px", // Máximo de altura
          overflowY: "auto", // Scroll vertical si es necesario
        }}
      >
        {/* Lista de asignaturas */}
        {filteredAsignaturas.length > 0 ? (
          filteredAsignaturas.map((item) => (
            <Box
              key={item.id_asignatura}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "4px 8px",
              }}
            >
              <Checkbox
                checked={selectedAsignaturas.includes(item.id_asignatura)}
                disabled={isAdding}
                onChange={() => handleCheckboxChange(item.id_asignatura)}
                sx={{
                  color: "#336B87",
                  "&.Mui-checked": { color: "#336B87" },
                  marginRight: "8px",
                }}
              />
              <Typography sx={{ flex: 1, color: "#000" }}>
                {item.nombre}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography color="#73767E" fontStyle="italic" textAlign="center">
            No hay más asignaturas disponibles
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default OtherAsignsMenu;
